import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Alert, Button, ConfirmDialog, Input, Modal, Select, Textarea } from '../../../components/ui';

const formFromController = (controller, defaultLocationId = '') => ({
    name: controller?.name || '',
    description: controller?.description || '',
    ipAddress: controller?.ipAddress || controller?.ip || '',
    macAddress: controller?.macAddress || '',
    status: controller?.status || 'Online',
    locationId: controller?.location?._id || controller?.location || defaultLocationId,
});

const PasControllerDialog = ({ isOpen, controller, createMode = false, defaultLocationId = '', onClose, onChanged, onDeleted }) => {
    const [form, setForm] = useState(formFromController(controller, defaultLocationId));
    const [locations, setLocations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setForm(formFromController(controller, defaultLocationId));
        setError('');
        setConfirmDelete(false);
        api.get('/api/locations')
            .then((response) => setLocations(response.data || []))
            .catch(() => setError('Topology locations could not be loaded.'));
    }, [isOpen, controller, defaultLocationId]);

    const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

    const save = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            const id = controller?._id || controller?.id;
            const response = createMode
                ? await api.post('/api/topology/controllers', form)
                : await api.put(`/api/topology/controllers/${id}`, form);
            await onChanged?.(response.data);
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'The controller could not be updated.');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        setError('');
        try {
            const id = controller?._id || controller?.id;
            await api.delete(`/api/topology/controllers/${id}`);
            setConfirmDelete(false);
            await onDeleted?.(controller);
            onClose();
        } catch (requestError) {
            setConfirmDelete(false);
            setError(requestError.response?.data?.message || 'The controller could not be deleted.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={createMode ? 'Add PAS controller' : 'Edit PAS controller'}
                subtitle={createMode ? 'Create a controller in a topology location' : controller?.description || controller?.name}
                size="md"
                footer={
                    <>
                        {!createMode && (
                            <Button variant="danger-soft" onClick={() => setConfirmDelete(true)} disabled={busy}>
                                Delete
                            </Button>
                        )}
                        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" form="pas-controller-form" loading={busy}>
                            {createMode ? 'Create controller' : 'Save controller'}
                        </Button>
                    </>
                }
            >
                {error && <Alert variant="error" title="Unable to save controller">{error}</Alert>}
                <form id="pas-controller-form" onSubmit={save} className="grid gap-4 sm:grid-cols-2">
                    <Input label="Controller name" value={form.name} onChange={change('name')} required />
                    <Select label="Status" value={form.status} onChange={change('status')} required>
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                        <option value="Maintenance">Maintenance</option>
                        <option value="Error">Error</option>
                    </Select>
                    <Input label="IP address" value={form.ipAddress} onChange={change('ipAddress')} />
                    <Input label="MAC address" value={form.macAddress} onChange={change('macAddress')} />
                    <Textarea
                        label="Description"
                        value={form.description}
                        onChange={change('description')}
                        rows={3}
                        className="sm:col-span-2"
                    />
                    <Select
                        label="Topology location"
                        value={form.locationId}
                        onChange={change('locationId')}
                        required
                        className="sm:col-span-2"
                    >
                        <option value="">Choose a location</option>
                        {locations.map((location) => (
                            <option key={location._id || location.id} value={location._id || location.id}>
                                {location.name}{location.code ? ` — ${location.code}` : ''}
                            </option>
                        ))}
                    </Select>
                    <p className="text-xs text-muted-fg sm:col-span-2">
                        Moving a controller also moves every attached device to the selected topology location.
                    </p>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
                title="Delete PAS controller?"
                message="A controller can only be deleted after its devices and zone groups have been moved or removed."
                confirmLabel="Delete controller"
                loading={busy}
            />
        </>
    );
};

export default PasControllerDialog;
